import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoChevronDown } from 'react-icons/io5';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

interface LanguageDialogProperties {
  onApply: (language: Language) => void;
  onCancel: () => void;
  currentLanguage: string;
  availableLanguages: Language[];
}

const NEXT_INDEX_OFFSET = 1;
const PREVIOUS_INDEX_OFFSET = 1;

function LanguageDropdown({
  selectedLanguage,
  onLanguageSelect,
  isOpen,
  onToggle,
  availableLanguages,
}: {
  selectedLanguage: Language;
  onLanguageSelect: (language: Language) => void;
  isOpen: boolean;
  onToggle: () => void;
  availableLanguages: Language[];
}): ReactElement {
  const dropdownReference = useRef<HTMLDivElement>(null);
  const selectedItemReference = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && selectedItemReference.current && dropdownReference.current) {
      const selectedItem = selectedItemReference.current;
      const dropdownContainer = dropdownReference.current;

      const itemTop = selectedItem.offsetTop;
      const itemBottom = itemTop + selectedItem.offsetHeight;
      const containerTop = dropdownContainer.scrollTop;
      const containerBottom = containerTop + dropdownContainer.clientHeight;

      if (itemTop < containerTop) {
        dropdownContainer.scrollTop = itemTop;
      } else if (itemBottom > containerBottom) {
        dropdownContainer.scrollTop = itemBottom - dropdownContainer.clientHeight;
      }
    }
  }, [isOpen, selectedLanguage]);

  const onDropdownItemClick = useCallback(
    (language: Language, event: React.MouseEvent) => {
      event.stopPropagation();
      onLanguageSelect(language);
      onToggle();
    },
    [onLanguageSelect, onToggle],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault();
          const currentIndex = availableLanguages.findIndex(
            (lang) => lang.code === selectedLanguage.code,
          );
          const nextIndex = (currentIndex + NEXT_INDEX_OFFSET) % availableLanguages.length;
          onLanguageSelect(availableLanguages[nextIndex]);
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const currentIndex = availableLanguages.findIndex(
            (lang) => lang.code === selectedLanguage.code,
          );
          const previousIndex =
            (currentIndex - PREVIOUS_INDEX_OFFSET + availableLanguages.length) %
            availableLanguages.length;
          onLanguageSelect(availableLanguages[previousIndex]);
          break;
        }
        default: {
          break;
        }
      }
    },
    [availableLanguages, selectedLanguage, onLanguageSelect],
  );

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={onToggle}
        onKeyDown={onKeyDown}
        className="flex w-full items-center justify-between rounded-xl border-2 border-neutral-grey-3 bg-neutral-white px-4 py-3 text-left shadow-custom hover:border-secondary-blue-3 focus:border-secondary-blue-3 focus:outline-none focus:ring-1 focus:ring-secondary-blue-3"
        tabIndex={0}
      >
        <div className="flex items-center">
          <div className="mr-3 flex h-8 w-8 items-center justify-center rounded bg-secondary-blue-5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-secondary-blue-3"
            >
              <path
                d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"
                fill="currentColor"
              />
            </svg>
          </div>
          <span className="font-medium text-neutral-grey-4">{selectedLanguage.nativeName}</span>
        </div>
        <IoChevronDown
          className={`h-5 w-5 text-neutral-grey-3 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownReference}
          className="absolute left-0 right-0 top-full z-10 mt-1 max-h-[15rem] overflow-y-scroll rounded-xl border border-neutral-grey-2 bg-neutral-white shadow-custom"
        >
          {availableLanguages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={(event) => onDropdownItemClick(language, event)}
              ref={selectedLanguage.code === language.code ? selectedItemReference : undefined}
              className="w-full border-b border-neutral-grey-1 p-2 text-left first:rounded-t-xl last:rounded-b-xl last:border-b-0 hover:bg-secondary-blue-5"
            >
              <span
                className={`flex h-[3.25em] w-full items-center rounded-lg px-3 font-medium text-neutral-grey-4 ${
                  selectedLanguage.code === language.code ? 'bg-neutral-grey-2' : ''
                }`}
              >
                {language.nativeName}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LanguageDialog({
  onApply,
  onCancel,
  currentLanguage,
  availableLanguages,
}: LanguageDialogProperties): ReactElement {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    availableLanguages.find((lang) => lang.code === currentLanguage) || availableLanguages[0],
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation('adminLogin');

  const onApplyClick = useCallback(() => {
    onApply(selectedLanguage);
  }, [selectedLanguage, onApply]);

  const onCancelClick = useCallback(() => {
    // Reset to current language
    setSelectedLanguage(
      availableLanguages.find((lang) => lang.code === currentLanguage) || availableLanguages[0],
    );
    onCancel();
  }, [currentLanguage, availableLanguages, onCancel]);

  const onLanguageSelect = useCallback((language: Language) => {
    setSelectedLanguage(language);
  }, []);

  const onToggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  return (
    <div className="mx-auto mt-6 w-full max-w-md">
      <div className="mb-2 flex flex-col items-center text-center">
        <h2 className="mb-2 font-lilita text-[2.5rem] text-neutral-white">{t('chooseLanguage')}</h2>
        <p className="max-w-[352px] text-center text-xl1 font-semibold text-neutral-grey-8">
          {t('selectLanguage')}
        </p>
      </div>
      <div className="my-8">
        <LanguageDropdown
          selectedLanguage={selectedLanguage}
          onLanguageSelect={onLanguageSelect}
          isOpen={isDropdownOpen}
          onToggle={onToggleDropdown}
          availableLanguages={availableLanguages}
        />
      </div>
      <div className="flex justify-center space-x-6">
        <button
          type="button"
          onClick={onCancelClick}
          className="min-w-[200px] rounded-full border-2 border-neutral-white px-8 py-5 text-[1.625rem] font-semibold text-neutral-white transition-colors duration-200 hover:border-secondary-blue-3"
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          onClick={onApplyClick}
          className="min-w-[200px] rounded-full bg-primary-yellow-3 px-8 py-5 text-[1.625rem] font-semibold text-primary-blue-3 shadow-custom transition-colors duration-200 hover:bg-primary-yellow-4"
        >
          {t('apply')}
        </button>
      </div>
    </div>
  );
}

export type { Language };
